<template>
	<div :class="bemm()">
		<header :class="bemm('header')">
			<Icon v-if="statusIcon" :class="bemm('icon')" :name="icon" />
			<h3 :class="bemm('title')">{{ title }}</h3>
		</header>

		<div :class="bemm('content')">
			<SafeHtml
				v-if="messageIsHtml"
				tag="div"
				:html="message"
				:class="bemm('message')"
			/>
			<p v-else :class="bemm('message')">{{ message }}</p>
		</div>

		<ButtonGroup :class="bemm('actions')" align="right">
			<Button
				:variant="ButtonVariant.OUTLINE"
				@click="handleCancel"
				:icon="cancelIcon"
				:class="bemm('cancel-button')"
			>
				{{ cancelLabel }}
			</Button>
			<Button
				:variant="ButtonVariant.DEFAULT"
				:color="confirmButtonColor"
				@click="handleConfirm"
				:icon="confirmIcon"
				:class="bemm('confirm-button')"
				ref="confirmButton"
				autofocus
			>
				{{ confirmLabel }}
			</Button>
		</ButtonGroup>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';
import { useBemm } from 'bemm';
import { ButtonGroup, ButtonVariant } from '../../Button';
import { Color, NotificationStatus, Status } from '@arritech/shared/types';
import { Icon, Icons, IconNameOrString } from '../../Icon';
import { Button } from '../../Button';
import { SafeHtml } from '../../SafeHtml';

interface Props {
	title: string;
	message: string;
	messageIsHtml?: boolean;
	icon?: IconNameOrString;
	onConfirm?: () => void | Promise<void>;
	confirmLabel?: string;
	confirmIcon?: IconNameOrString;
	confirmColor?: Color | string;
	onCancel?: () => void | Promise<void>;
	cancelLabel?: string;
	cancelIcon?: IconNameOrString;
	popupId?: string;
	status?: NotificationStatus | Status;
}

const props = withDefaults(defineProps<Props>(), {
	messageIsHtml: false,
	icon: Icons.ALERT,
	confirmLabel: 'Confirm',
	confirmIcon: Icons.CHECK,
	cancelLabel: 'Cancel',
	cancelIcon: Icons.CLEAR,
});

const emit = defineEmits<{
	close: [];
}>();

const bemm = useBemm('confirm-dialog', {
	return: 'string',
	includeBaseClass: true,
});
const confirmButton = ref<any>(null);
const confirmButtonColor = computed(() => {
	if (props.confirmColor) return props.confirmColor;

	return props.confirmIcon === Icons.DELETE ? Color.ERROR : undefined;
});

const handleConfirm = async () => {
	try {
		await props.onConfirm?.();
	} catch {
		// Confirm dialogs should always close after user confirmation.
	} finally {
		emit('close');
	}
};

const handleCancel = async () => {
	try {
		await props.onCancel?.();
	} catch {
		// Cancel dialogs should always close after user cancellation.
	} finally {
		emit('close');
	}
};

const statusIcon = computed(() => {
	if (props.icon) return props.icon;

	if (!props.status) return null;

	switch (props.status) {
		case NotificationStatus.SUCCESS:
			return Icons.CHECK_CIRCLE;
		case NotificationStatus.ERROR:
			return Icons.DELETE;
		case NotificationStatus.WARNING:
			return Icons.EXCLAMATION;
		case NotificationStatus.INFO:
			return Icons.INFO;
		default:
			return null;
	}
});

// Focus the confirm button when mounted and handle enter key
onMounted(() => {
	nextTick(() => {
		// Focus the confirm button for keyboard navigation
		const button = confirmButton.value?.$el as HTMLElement;
		if (button) {
			const buttonElement = button.querySelector('button') || button;
			buttonElement?.focus();
		}
	});
});

// Handle keyboard events
const handleKeydown = (event: KeyboardEvent) => {
	if (event.key === 'Enter') {
		event.preventDefault();
		handleConfirm();
	} else if (event.key === 'Escape') {
		event.preventDefault();
		handleCancel();
	}
};

// Add event listener when component mounts
onMounted(() => {
	document.addEventListener('keydown', handleKeydown);
});

// Clean up event listener
onUnmounted(() => {
	document.removeEventListener('keydown', handleKeydown);
});
</script>

<style lang="scss">
.confirm-dialog {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1.5rem;
	min-width: 240px;
	max-width: 360px;
	text-align: center;

	&__icon {
		color: var(--color-warning);
		font-size: 2em;
	}

	&__header {
		display: flex;
		gap: var(--space-s);
		align-items: center;
		justify-content: center;
		width: 100%;
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		text-align: left;
	}

	&__title {
		margin: 0;
		font-size: 1.25rem;
		font-weight: var(--font-weight-bold);
		color: var(--color-foreground);
	}

	&__message {
		margin: 0;
		color: color-mix(in srgb, var(--color-foreground), transparent 35%);
		line-height: 1.5;
		white-space: pre-line;
	}

	&__actions {
		display: flex;
		gap: 1rem;
		// justify-content: center;
		width: 100%;
	}
}

// Mobile responsiveness
@media (max-width: 768px) {
	.confirm-dialog {
		min-width: auto;
		width: 100%;

		&__actions {
			flex-direction: column-reverse;
		}

		&__cancel-button,
		&__confirm-button {
			width: 100%;
		}
	}
}
</style>
